import {
    PDFDict,
    PDFDocument,
    PDFName,
    PDFNumber,
    concatTransformationMatrix,
    drawObject,
    popGraphicsState,
    pushGraphicsState,
} from 'pdf-lib';
import type { PDFPage } from 'pdf-lib';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { VORANK_EINLESEN_MAX_BYTES } from '../vorank/vorankUnterschriftTypes';
import type { VorankDrehung, VorankEinleseAuswahl, VorankEinleseSeite } from '../vorank/vorankUnterschriftTypes';
import {
    BILD_LANGE_KANTE_MAX_PX,
    addiereDrehung,
    bildZielPixel,
    drehungVorschlag,
    einbettungsMatrix,
    einpassungA4,
    normiereDrehung,
} from './vorankScanMasse';

/**
 * Papierweg (docs/vorankuendigung-unterschrift-plan.md §8): eine
 * unterschrieben zurückgekommene Vorankündigung — Adobe-signiert, gescannt
 * oder fotografiert — wird zu einer A4-PDF, die der Plot (`vorankPlotBlatt`)
 * wie die eigene Vorankündigung verarbeitet und die an die Behörde gehen kann.
 *
 * Je gewählter Quellseite entsteht eine A4-HOCH-Seite (595,28 × 841,89 pt),
 * der Inhalt mit der gewählten Drehung eingepasst und mittig, nie verzerrt.
 *
 * Zwei Fallstricke bestimmen den Weg einer Seite:
 * - **Adobe:** `embedPdf` übernimmt nur den Seiteninhalt, KEINE Annotationen.
 *   Eine Unterschrift aus „Ausfüllen und unterschreiben“ (FreeText/Ink/Stamp)
 *   oder ein Zertifikats-Signaturfeld (Widget) fehlte dann im Plot. Seiten mit
 *   sichtbaren Annotationen werden deshalb mit pdf.js samt Annotationen
 *   gerastert (200 dpi, JPEG 0,85). Seiten ohne bleiben Vektor.
 * - **Handyfoto:** pdf-lib kennt keine EXIF-Drehung. Bilder gehen über
 *   `createImageBitmap(…, { imageOrientation: 'from-image' })` und Canvas.
 *
 * Aufteilung: Erkennung, Entscheidung und der Zusammenbau mit pdf-lib laufen
 * in Node (Tests); nur pdf.js-Rendern und Canvas brauchen den Browser und
 * stehen in schmalen Funktionen am Ende.
 */

/** pt je mm */
const MM = 72 / 25.4;
const A4_BREITE_PT = 595.28;
const A4_HOEHE_PT = 841.89;

/** Rasterauflösung für Seiten mit Annotationen. */
const RASTER_DPI = 200;
const JPEG_QUALITAET = 0.85;
/** Fotos haben keine Papiergröße — gerechnet wird wie ein A4-Scan mit 300 dpi. */
const BILD_DPI = 300;
/** Breite der Vorschaubilder im Dialog. */
const VORSCHAU_BREITE_PX = 160;

const TITEL = 'Vorankündigung (unterschrieben)';

// ═══════════════════════════════════════════════════════════════════════════
// Dateiart
// ═══════════════════════════════════════════════════════════════════════════

export type VorankDateiArt = 'pdf' | 'jpeg' | 'png' | 'heic' | 'unbekannt';

const HEIF_MARKEN = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1']);

/**
 * Art einer gewählten Datei — nach dem Dateikopf, nicht nach der Endung (ein
 * Handy nennt ein JPEG gern „.HEIC“ und umgekehrt). Die Endung zählt nur, wenn
 * der Kopf nichts verrät.
 */
export function erkenneDateiArt(kopf: Uint8Array, name: string, mime: string): VorankDateiArt {
    const ascii = (von: number, bis: number) => String.fromCharCode(...kopf.subarray(von, bis));
    // „%PDF“ darf ein paar Bytes Müll vor sich haben (Scanner, Mailprogramme).
    const vorn = String.fromCharCode(...kopf.subarray(0, Math.min(kopf.length, 1024)));
    if (vorn.includes('%PDF-')) return 'pdf';
    if (kopf[0] === 0xff && kopf[1] === 0xd8 && kopf[2] === 0xff) return 'jpeg';
    if (kopf[0] === 0x89 && ascii(1, 4) === 'PNG' && kopf[4] === 0x0d && kopf[5] === 0x0a) return 'png';
    if (kopf.length >= 12 && ascii(4, 8) === 'ftyp' && HEIF_MARKEN.has(ascii(8, 12))) return 'heic';
    const n = (name || '').toLowerCase();
    const m = (mime || '').toLowerCase();
    if (/\.(heic|heif)$/.test(n) || m === 'image/heic' || m === 'image/heif') return 'heic';
    return 'unbekannt';
}

const MB = 1024 * 1024;

/** Größe und Art prüfen; der Fehlertext nennt die Datei und den Ausweg. */
export function pruefeEinleseDatei(datei: { name: string; size: number }, art: VorankDateiArt): void {
    const name = datei.name || 'Die Datei';
    if (datei.size > VORANK_EINLESEN_MAX_BYTES) {
        const mb = (datei.size / MB).toFixed(1).replace('.', ',');
        throw new Error(
            `„${name}“ ist ${mb} MB groß — höchstens ${VORANK_EINLESEN_MAX_BYTES / MB} MB je Datei. `
            + 'Bitte kleiner als PDF, JPG oder PNG speichern.',
        );
    }
    if (art === 'heic') {
        throw new Error(`„${name}“ ist ein HEIC-Foto (iPhone) — bitte als PDF, JPG oder PNG speichern und erneut wählen.`);
    }
    if (art === 'unbekannt') {
        throw new Error(`„${name}“ lässt sich nicht einlesen — bitte als PDF, JPG oder PNG wählen.`);
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// PDF lesen und Annotationen erkennen (pdf-lib, Node-fähig)
// ═══════════════════════════════════════════════════════════════════════════

/** Eine PDF-Seite, wie der Dialog sie braucht. Maße so, wie die Seite angezeigt wird (nach /Rotate). */
export interface VorankPdfSeitenInfo {
    breiteMm: number;
    hoeheMm: number;
    /** /Rotate der Seite. */
    seitenDrehung: VorankDrehung;
    /** Sichtbare Annotationen außer Link/Popup (Widget, Ink, Stamp, FreeText …). */
    hatAnnotationen: boolean;
    /** Mindestens eine davon ohne Druck-Flag — dann wird für die Anzeige gerastert, nicht für den Druck. */
    annotationenOhneDruck: boolean;
    /** Die Seite hat einen Content-Stream (eine ganz leere Seite lässt sich nicht als Vektor einbetten). */
    hatInhalt: boolean;
}

/** Annotation-Flags (PDF 32000, Tabelle 165). */
const FLAG_HIDDEN = 2;
const FLAG_PRINT = 4;

/** Annotationen, die nichts aufs Blatt bringen: Verweise und aufklappende Notizfenster. */
const OHNE_DARSTELLUNG = new Set([PDFName.of('Link'), PDFName.of('Popup')]);

/** Zählt die sichtbaren Annotationen einer Seite — und wie viele davon nicht gedruckt würden. */
export function sichtbareAnnotationen(seite: PDFPage): { sichtbar: number; ohneDruck: number } {
    const annots = seite.node.Annots();
    let sichtbar = 0;
    let ohneDruck = 0;
    if (!annots) return { sichtbar, ohneDruck };
    for (let i = 0; i < annots.size(); i++) {
        const annot = annots.lookup(i);
        if (!(annot instanceof PDFDict)) continue;
        const art = annot.lookup(PDFName.of('Subtype'));
        if (art instanceof PDFName && OHNE_DARSTELLUNG.has(art)) continue;
        const f = annot.lookup(PDFName.of('F'));
        const flags = f instanceof PDFNumber ? f.asNumber() : 0;
        if (flags & FLAG_HIDDEN) continue;
        sichtbar++;
        if (!(flags & FLAG_PRINT)) ohneDruck++;
    }
    return { sichtbar, ohneDruck };
}

/** Rechteck aus pdf-lib (Koordinaten dürfen vertauscht sein) → links/unten/Breite/Höhe. */
const normiereRechteck = (r: { x: number; y: number; width: number; height: number }) => ({
    x: Math.min(r.x, r.x + r.width),
    y: Math.min(r.y, r.y + r.height),
    breite: Math.abs(r.width),
    hoehe: Math.abs(r.height),
});

/** Sichtbarer Bereich der Seite in pt: CropBox, beschnitten auf die MediaBox. */
export function seitenRahmen(seite: PDFPage): { x: number; y: number; breite: number; hoehe: number } {
    const media = normiereRechteck(seite.getMediaBox());
    const crop = normiereRechteck(seite.getCropBox());
    const x = Math.max(media.x, crop.x);
    const y = Math.max(media.y, crop.y);
    const rechts = Math.min(media.x + media.breite, crop.x + crop.breite);
    const oben = Math.min(media.y + media.hoehe, crop.y + crop.hoehe);
    // Liegt die CropBox daneben (kaputte Datei), gilt die MediaBox.
    if (!(rechts > x && oben > y)) return media;
    return { x, y, breite: rechts - x, hoehe: oben - y };
}

export async function ladePdfQuelle(bytes: Uint8Array, name = 'Die PDF'): Promise<PDFDocument> {
    try {
        // ignoreEncryption: auch eine Datei mit Rechte-Kennwort (Adobe) lässt
        // sich lesen; ihre Seiten werden dann gerastert (pdf.js entschlüsselt).
        return await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    } catch {
        throw new Error(`„${name}“ lässt sich nicht als PDF lesen.`);
    }
}

export function pdfSeitenInfo(dok: PDFDocument): VorankPdfSeitenInfo[] {
    return dok.getPages().map((seite) => {
        const rahmen = seitenRahmen(seite);
        const seitenDrehung = normiereDrehung(seite.getRotation().angle);
        const quer = seitenDrehung === 90 || seitenDrehung === 270;
        const { sichtbar, ohneDruck } = sichtbareAnnotationen(seite);
        return {
            breiteMm: (quer ? rahmen.hoehe : rahmen.breite) / MM,
            hoeheMm: (quer ? rahmen.breite : rahmen.hoehe) / MM,
            seitenDrehung,
            hatAnnotationen: sichtbar > 0,
            annotationenOhneDruck: ohneDruck > 0,
            hatInhalt: seite.node.Contents() !== undefined,
        };
    });
}

/**
 * Vektor oder Raster? Gerastert wird, was als Vektor etwas verlöre: Seiten
 * mit Annotationen (Unterschrift!), verschlüsselte Dateien (pdf-lib kann den
 * Inhalt nicht entschlüsseln) und Seiten ohne Inhalt (pdf-lib bettet sie
 * nicht ein).
 */
export function mussGerastertWerden(info: Pick<VorankPdfSeitenInfo, 'hatAnnotationen' | 'hatInhalt'>, verschluesselt: boolean): boolean {
    return verschluesselt || info.hatAnnotationen || !info.hatInhalt;
}

// ═══════════════════════════════════════════════════════════════════════════
// A4-PDF zusammenbauen (pdf-lib, Node-fähig)
// ═══════════════════════════════════════════════════════════════════════════

export type VorankA4Teil =
    /** Seite einer PDF ohne Annotationen — bleibt Vektor. */
    | { art: 'vektor'; quelle: PDFDocument; seitenIndex: number; drehung: VorankDrehung }
    /** Gerasterte Seite oder Foto, schon aufrecht (EXIF/Rotate eingerechnet). */
    | { art: 'jpeg'; bytes: Uint8Array; drehung: VorankDrehung };

/** Je Teil eine A4-Hochseite, Inhalt eingepasst und mittig. */
export async function baueA4Pdf(teile: readonly VorankA4Teil[]): Promise<Uint8Array> {
    if (teile.length === 0) throw new Error('Es ist keine Seite ausgewählt — bitte mindestens eine Seite mitnehmen.');

    // updateMetadata: false — sonst trägt pdf-lib sich als Ersteller und Produzent ein.
    const ziel = await PDFDocument.create({ updateMetadata: false });
    ziel.setTitle(TITEL);

    for (const [i, teil] of teile.entries()) {
        const blatt = ziel.addPage([A4_BREITE_PT, A4_HOEHE_PT]);
        let name: PDFName;
        let matrix: ReturnType<typeof einbettungsMatrix>;

        if (teil.art === 'vektor') {
            if (teil.quelle.isEncrypted) throw new Error('Eine verschlüsselte PDF-Seite lässt sich nicht als Vektor übernehmen.');
            const seite = teil.quelle.getPages()[teil.seitenIndex];
            if (!seite) throw new Error(`Seite ${teil.seitenIndex + 1} gibt es in der Datei nicht.`);
            const r = seitenRahmen(seite);
            // Die eingebettete Seite kennt ihr /Rotate nicht mehr — es kommt zur
            // gewählten Drehung dazu, gerechnet auf die ungedrehten Maße.
            const drehung = addiereDrehung(normiereDrehung(seite.getRotation().angle), teil.drehung);
            const e = einpassungA4({ breiteMm: r.breite / MM, hoeheMm: r.hoehe / MM }, drehung);
            let eingebettet;
            try {
                eingebettet = await ziel.embedPage(seite, { left: r.x, bottom: r.y, right: r.x + r.breite, top: r.y + r.hoehe });
                // Sonst erst beim Speichern — ein Fehler käme dann englisch aus save().
                await eingebettet.embed();
            } catch {
                throw new Error(`Seite ${teil.seitenIndex + 1} lässt sich nicht übernehmen.`);
            }
            name = blatt.node.newXObject('Seite', eingebettet.ref);
            matrix = einbettungsMatrix({
                drehung,
                inhaltB: r.breite,
                inhaltH: r.hoehe,
                skalaX: e.skala,
                skalaY: e.skala,
                x0: e.xMm * MM,
                y0: A4_HOEHE_PT - (e.yMm + e.hoeheMm) * MM,
            });
        } else {
            let bild;
            try {
                bild = await ziel.embedJpg(teil.bytes);
            } catch {
                throw new Error(`Bild ${i + 1} lässt sich nicht übernehmen.`);
            }
            // Ein Bild zeichnet sich ins Einheitsquadrat; die Skala ist die gedruckte Größe.
            const e = einpassungA4({ breiteMm: bild.width, hoeheMm: bild.height }, teil.drehung);
            name = blatt.node.newXObject('Bild', bild.ref);
            matrix = einbettungsMatrix({
                drehung: teil.drehung,
                inhaltB: 1,
                inhaltH: 1,
                skalaX: bild.width * e.skala * MM,
                skalaY: bild.height * e.skala * MM,
                x0: e.xMm * MM,
                y0: A4_HOEHE_PT - (e.yMm + e.hoeheMm) * MM,
            });
        }

        blatt.pushOperators(
            pushGraphicsState(),
            concatTransformationMatrix(...matrix),
            drawObject(name),
            popGraphicsState(),
        );
    }

    return ziel.save();
}

// ═══════════════════════════════════════════════════════════════════════════
// Browser: pdf.js und Canvas
// ═══════════════════════════════════════════════════════════════════════════

type Pdfjs = typeof import('pdfjs-dist');

let pdfjsLaden: Promise<Pdfjs> | null = null;

/** pdf.js erst bei Bedarf und nur einmal; Worker aus dem Bundle wie in NativePdfSeiten/PdfCropper — nur setzen, falls noch leer. */
const ladePdfjs = (): Promise<Pdfjs> => {
    pdfjsLaden ??= Promise.all([
        import('pdfjs-dist'),
        import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]).then(([pdfjs, worker]) => {
        if (!pdfjs.GlobalWorkerOptions.workerSrc) pdfjs.GlobalWorkerOptions.workerSrc = worker.default as string;
        return pdfjs;
    }).catch((e: unknown) => {
        // Ein Netzfehler beim Nachladen darf den nächsten Versuch nicht blockieren.
        pdfjsLaden = null;
        throw e;
    });
    return pdfjsLaden;
};

/** pdf.js-Dokument einer Datei: erst bei Bedarf geöffnet, am Ende wieder geschlossen. */
class PdfjsDokument {
    private offen: Promise<PDFDocumentProxy> | null = null;

    constructor(private readonly bytes: Uint8Array, private readonly name: string) {}

    holen(): Promise<PDFDocumentProxy> {
        this.offen ??= ladePdfjs().then(async (pdfjs) => {
            try {
                // Kopie: pdf.js reicht den Puffer an den Worker weiter und leert ihn dabei.
                return await pdfjs.getDocument({ data: this.bytes.slice() }).promise;
            } catch (e) {
                if (e instanceof Error && e.name === 'PasswordException') {
                    throw new Error(`„${this.name}“ ist mit einem Kennwort geschützt — bitte ohne Kennwort speichern oder ausdrucken und scannen.`);
                }
                throw new Error(`„${this.name}“ lässt sich nicht als PDF öffnen.`);
            }
        });
        return this.offen;
    }

    schliessen(): void {
        this.offen?.then(pdf => pdf.destroy()).catch(() => undefined);
        this.offen = null;
    }
}

const leinwand = (breite: number, hoehe: number) => {
    const canvas = document.createElement('canvas');
    canvas.width = breite;
    canvas.height = hoehe;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar.');
    // Weißer Grund, sonst werden transparente Flächen im JPEG schwarz.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, breite, hoehe);
    return { canvas, ctx };
};

/** Speicher sofort freigeben — ältere Geräte halten sonst je Seite viele MB. */
const freigeben = (canvas: HTMLCanvasElement) => {
    canvas.width = 0;
    canvas.height = 0;
};

const alsJpeg = (canvas: HTMLCanvasElement): Promise<Uint8Array> =>
    new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Die Seite ließ sich nicht als Bild ablegen.'));
                return;
            }
            blob.arrayBuffer().then(b => resolve(new Uint8Array(b)), reject);
        }, 'image/jpeg', JPEG_QUALITAET);
    });

const vorschauAus = (canvas: HTMLCanvasElement): string => {
    const url = canvas.toDataURL('image/jpeg', 0.8);
    freigeben(canvas);
    return url;
};

/**
 * Eine PDF-Seite auf Canvas, SAMT Annotationen: Druck-Intent wie beim
 * Drucken aus pdf.js (`ENABLE_STORAGE` — Formular- und Signaturfelder kommen
 * mit ihrer Darstellung aufs Bild). Annotationen ohne Druck-Flag zeigt nur der
 * Anzeige-Intent; dann wird der genommen, damit nichts verloren geht.
 * `breitePx` bekommt die Seitengröße in pt (nach /Rotate) und nennt die Zielbreite.
 */
const rendereSeite = async (
    pdf: PDFDocumentProxy,
    seitenNr: number,
    breitePx: (breitePt: number, hoehePt: number) => number,
    anzeige: boolean,
): Promise<HTMLCanvasElement> => {
    const pdfjs = await ladePdfjs();
    const seite = await pdf.getPage(seitenNr);
    try {
        const basis = seite.getViewport({ scale: 1 });
        const viewport = seite.getViewport({ scale: breitePx(basis.width, basis.height) / basis.width });
        const { canvas, ctx } = leinwand(Math.max(1, Math.round(viewport.width)), Math.max(1, Math.round(viewport.height)));
        await seite.render({
            canvasContext: ctx,
            viewport,
            intent: anzeige ? 'display' : 'print',
            annotationMode: anzeige ? pdfjs.AnnotationMode.ENABLE : pdfjs.AnnotationMode.ENABLE_STORAGE,
        }).promise;
        return canvas;
    } finally {
        seite.cleanup();
    }
};

/** 200 dpi, lange Kante höchstens 3508 px. */
const rasterBreite = (breitePt: number, hoehePt: number): number =>
    bildZielPixel((breitePt / 72) * RASTER_DPI, (hoehePt / 72) * RASTER_DPI).breitePx;

interface GeladenesBild {
    quelle: CanvasImageSource;
    breite: number;
    hoehe: number;
    schliessen: () => void;
}

/**
 * Bild aufrecht laden: `createImageBitmap` mit `imageOrientation: 'from-image'`
 * dreht nach EXIF (Handyfoto!). Rückfall `<img>` — aktuelle Browser drehen auch
 * dort nach EXIF (CSS `image-orientation: from-image` ist Vorgabe).
 */
const ladeBild = async (datei: Blob, name: string): Promise<GeladenesBild> => {
    if (typeof createImageBitmap === 'function') {
        try {
            const bmp = await createImageBitmap(datei, { imageOrientation: 'from-image' });
            return { quelle: bmp, breite: bmp.width, hoehe: bmp.height, schliessen: () => bmp.close() };
        } catch {
            // weiter mit <img>
        }
    }
    const url = URL.createObjectURL(datei);
    try {
        const img = new Image();
        img.src = url;
        await img.decode();
        return { quelle: img, breite: img.naturalWidth, hoehe: img.naturalHeight, schliessen: () => URL.revokeObjectURL(url) };
    } catch {
        URL.revokeObjectURL(url);
        throw new Error(`„${name}“ lässt sich nicht als Bild öffnen — bitte als PDF, JPG oder PNG neu speichern.`);
    }
};

/** Bild auf Canvas, lange Kante höchstens `langeKante` px; der Aufrufer gibt die Leinwand frei. */
const bildAufLeinwand = (bild: GeladenesBild, langeKante: number): HTMLCanvasElement => {
    const ziel = bildZielPixel(bild.breite, bild.hoehe, langeKante);
    const { canvas, ctx } = leinwand(ziel.breitePx, ziel.hoehePx);
    ctx.drawImage(bild.quelle, 0, 0, ziel.breitePx, ziel.hoehePx);
    return canvas;
};

// ═══════════════════════════════════════════════════════════════════════════
// Öffentliche Einstiege für den Dialog
// ═══════════════════════════════════════════════════════════════════════════

interface DateiQuelle {
    name: string;
    art: VorankDateiArt;
}

/** Art prüfen, bevor die ganze Datei gelesen wird — der Kopf genügt. */
const pruefeDatei = async (datei: File, index: number): Promise<DateiQuelle> => {
    const name = datei.name || `Datei ${index + 1}`;
    const kopf = new Uint8Array(await datei.slice(0, 1024).arrayBuffer());
    const art = erkenneDateiArt(kopf, datei.name, datei.type);
    pruefeEinleseDatei({ name, size: datei.size }, art);
    return { name, art };
};

interface PdfQuelle {
    /** null: pdf-lib liest die Datei nicht — dann wird jede Seite gerastert. */
    dok: PDFDocument | null;
    infos: VorankPdfSeitenInfo[];
    pdfjs: PdfjsDokument;
}

/** PDF öffnen: Info je Seite aus pdf-lib; liest pdf-lib die Datei nicht, aus pdf.js. */
const oeffnePdf = async (datei: File, name: string): Promise<PdfQuelle> => {
    const bytes = new Uint8Array(await datei.arrayBuffer());
    const pdfjs = new PdfjsDokument(bytes, name);
    try {
        const dok = await ladePdfQuelle(bytes, name);
        return { dok, infos: pdfSeitenInfo(dok), pdfjs };
    } catch {
        const pdf = await pdfjs.holen();
        const infos: VorankPdfSeitenInfo[] = [];
        for (let nr = 1; nr <= pdf.numPages; nr++) {
            const seite = await pdf.getPage(nr);
            const v = seite.getViewport({ scale: 1 });
            const annots: { subtype?: string }[] = await seite.getAnnotations();
            infos.push({
                breiteMm: v.width / MM,
                hoeheMm: v.height / MM,
                seitenDrehung: normiereDrehung(seite.rotate),
                hatAnnotationen: annots.some(a => a.subtype !== 'Link' && a.subtype !== 'Popup'),
                annotationenOhneDruck: false,
                hatInhalt: false,
            });
            seite.cleanup();
        }
        return { dok: null, infos, pdfjs };
    }
};

/**
 * Seiten der gewählten Dateien für Vorschau und Auswahl, in Dateireihenfolge.
 * Fehler (zu groß, HEIC, keine PDF/JPG/PNG, Kennwort) mit Dateinamen und
 * Ausweg. Ein Vorschaubild, das sich nicht rendern lässt, bleibt leer
 * (`vorschau: ''`) — die Seite ist trotzdem wählbar.
 */
export async function leseEinleseSeiten(dateien: readonly File[]): Promise<VorankEinleseSeite[]> {
    const seiten: VorankEinleseSeite[] = [];

    for (const [dateiIndex, datei] of dateien.entries()) {
        const { name, art } = await pruefeDatei(datei, dateiIndex);

        if (art === 'pdf') {
            const quelle = await oeffnePdf(datei, name);
            try {
                const pdf = await quelle.pdfjs.holen();
                for (const [seitenIndex, info] of quelle.infos.entries()) {
                    let vorschau = '';
                    try {
                        vorschau = vorschauAus(await rendereSeite(pdf, seitenIndex + 1, () => VORSCHAU_BREITE_PX, info.annotationenOhneDruck));
                    } catch (e) {
                        console.warn(`Vorankündigung einlesen: keine Vorschau für „${name}“, Seite ${seitenIndex + 1}.`, e);
                    }
                    seiten.push({
                        dateiIndex,
                        seitenIndex,
                        breiteMm: info.breiteMm,
                        hoeheMm: info.hoeheMm,
                        hatAnnotationen: info.hatAnnotationen,
                        drehungVorschlag: drehungVorschlag(info.breiteMm, info.hoeheMm),
                        vorschau,
                    });
                }
            } finally {
                quelle.pdfjs.schliessen();
            }
            continue;
        }

        const bild = await ladeBild(datei, name);
        try {
            const breiteMm = (bild.breite / BILD_DPI) * 25.4;
            const hoeheMm = (bild.hoehe / BILD_DPI) * 25.4;
            let vorschau = '';
            try {
                // Lange Kante so, dass die Breite etwa VORSCHAU_BREITE_PX wird.
                const langeKante = Math.round(VORSCHAU_BREITE_PX * Math.max(bild.breite, bild.hoehe) / bild.breite);
                vorschau = vorschauAus(bildAufLeinwand(bild, langeKante));
            } catch (e) {
                console.warn(`Vorankündigung einlesen: keine Vorschau für „${name}“.`, e);
            }
            seiten.push({
                dateiIndex,
                seitenIndex: 0,
                breiteMm,
                hoeheMm,
                hatAnnotationen: false,
                drehungVorschlag: drehungVorschlag(breiteMm, hoeheMm),
                vorschau,
            });
        } finally {
            bild.schliessen();
        }
    }
    return seiten;
}

/**
 * Die gewählten Seiten als EINE A4-PDF (je Seite A4 hoch, Titel
 * „Vorankündigung (unterschrieben)“). Seiten ohne Annotationen bleiben Vektor;
 * Seiten mit Annotationen und Fotos werden JPEG (0,85).
 */
export async function normalisiereAufA4(dateien: readonly File[], auswahl: VorankEinleseAuswahl): Promise<Uint8Array> {
    if (!auswahl.seiten.length) throw new Error('Es ist keine Seite ausgewählt — bitte mindestens eine Seite mitnehmen.');

    const pdfQuellen = new Map<number, PdfQuelle>();
    const teile: VorankA4Teil[] = [];

    try {
        for (const s of auswahl.seiten) {
            const datei = dateien[s.dateiIndex];
            if (!datei) throw new Error('Eine gewählte Datei fehlt — bitte die Dateien neu wählen.');
            const { name, art } = await pruefeDatei(datei, s.dateiIndex);

            let canvas: HTMLCanvasElement;
            if (art === 'pdf') {
                let quelle = pdfQuellen.get(s.dateiIndex);
                if (!quelle) {
                    quelle = await oeffnePdf(datei, name);
                    pdfQuellen.set(s.dateiIndex, quelle);
                }
                const info = quelle.infos[s.seitenIndex];
                if (!info) throw new Error(`„${name}“ hat keine Seite ${s.seitenIndex + 1}.`);

                if (quelle.dok && !mussGerastertWerden(info, quelle.dok.isEncrypted)) {
                    teile.push({ art: 'vektor', quelle: quelle.dok, seitenIndex: s.seitenIndex, drehung: s.drehung });
                    continue;
                }
                canvas = await rendereSeite(await quelle.pdfjs.holen(), s.seitenIndex + 1, rasterBreite, info.annotationenOhneDruck);
            } else {
                const bild = await ladeBild(datei, name);
                try {
                    canvas = bildAufLeinwand(bild, BILD_LANGE_KANTE_MAX_PX);
                } finally {
                    bild.schliessen();
                }
            }

            try {
                teile.push({ art: 'jpeg', bytes: await alsJpeg(canvas), drehung: s.drehung });
            } finally {
                freigeben(canvas);
            }
        }

        return await baueA4Pdf(teile);
    } finally {
        for (const quelle of pdfQuellen.values()) quelle.pdfjs.schliessen();
    }
}
